import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

/**
 * EntitlementContext - single source of truth for the freemium state.
 *
 * Exposes:
 *  - `entitlement`  - per-user snapshot from GET /api/entitlement.
 *  - `config`       - public copy/limits from GET /api/config/freemium.
 *  - `refresh()`    - refetch after a session completes (or upgrade).
 *  - `track(event, properties?)` - analytics helper wired to POST /analytics/track.
 *
 * `isFree` and `isPaid` are convenience derivations. Handles unauthenticated
 * gracefully - `entitlement` is null until the user logs in, `config` still
 * loads because it's public.
 */
const EntitlementContext = createContext(null);

const DEFAULT_CONFIG = {
  free_video_questions: 1,
  free_live_questions: 1,
  free_predicted_visible: 5,
  upgrade_after: "live_complete",
  unlock_score_mode: "preview",
  upgrade_headline: "Great start!",
  upgrade_subheadline: "You've completed your free interview experience.",
  upgrade_cta_primary: "Continue Preparing",
  upgrade_cta_secondary: "Maybe Later",
};

export function EntitlementProvider({ children }) {
  const { user } = useAuth();
  const [entitlement, setEntitlement] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Public config - always fetch.
      const cfg = await api.get("/config/freemium").then((r) => r.data).catch(() => DEFAULT_CONFIG);
      setConfig(cfg || DEFAULT_CONFIG);
      // Entitlement - requires auth cookie.
      if (user) {
        const ent = await api.get("/entitlement").then((r) => r.data).catch(() => null);
        setEntitlement(ent);
      } else {
        setEntitlement(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const track = useCallback(async (event, properties = {}, workspace_id = null) => {
    // Fire-and-forget. Never surface errors to the UI - analytics must not
    // break the app. Unauthenticated events are dropped silently (the backend
    // requires auth). We could enqueue them client-side for retry later, but
    // Phase 1 keeps it simple.
    if (!user || !event) return;
    try {
      await api.post("/analytics/track", { event, properties, workspace_id });
    } catch {
      /* silent */
    }
  }, [user]);

  const value = useMemo(() => {
    const isFree = !!entitlement?.is_free;
    const isPaid = !!entitlement?.is_paid;
    return {
      entitlement,
      config,
      loading,
      refresh,
      track,
      isFree,
      isPaid,
      // Convenience helpers for the common gates.
      canStartVideo: entitlement?.video?.allowed ?? false,
      canStartLive: entitlement?.live?.allowed ?? false,
      videoRemaining: entitlement?.video?.remaining ?? null,
      liveRemaining: entitlement?.live?.remaining ?? null,
      predictedVisible: entitlement?.predicted?.visible ?? null,
      unlockScoreMode: entitlement?.unlock_score_mode ?? "preview",
    };
  }, [entitlement, config, loading, refresh, track]);

  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
}

export function useEntitlement() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error("useEntitlement must be used inside <EntitlementProvider>");
  return ctx;
}
